import React from 'react';

// Among Us crewmate SVG art for special items (Mythic, Galaxy, Pass, Rank Rewards)
// Each design is a stylized crewmate silhouette with unique visual effects

const CREWMATE_PATH = "M 16 4 C 11 4 7 8 7 13 L 7 25 C 7 28 9 30 12 30 L 12 33 C 12 34.5 13.5 35.5 15 35.5 L 16.5 35.5 L 16.5 30.5 L 19.5 30.5 L 19.5 35.5 L 21 35.5 C 22.5 35.5 24 34.5 24 33 L 24 30 C 27 30 29 28 29 25 L 29 13 C 29 8 25 4 20 4 Z";
const VISOR_PATH = "M 21 9 C 24 9 27 11 27 14 C 27 17 24 19 21 19 L 19 19 C 16 19 14 17 14 14 C 14 11 16 9 19 9 Z";

// Helper: creates a crewmate SVG component with custom colors and optional effects
function makeCrewmate(id, { bodyColors, visorColors, stroke, glow = 1, effects = null, radial = false }) {
  return ({ size = 48 }) => (
    <svg viewBox="0 0 36 40" width={size} height={size * 40/36}>
      <defs>
        {radial ? (
          <radialGradient id={`${id}_b`} cx="0.5" cy="0.4" r="0.6">
            {bodyColors.map((c, i) => (
              <stop key={i} offset={`${(i / Math.max(bodyColors.length - 1, 1)) * 100}%`} stopColor={c}/>
            ))}
          </radialGradient>
        ) : (
          <linearGradient id={`${id}_b`} x1="0" y1="0" x2="1" y2="1">
            {bodyColors.map((c, i) => (
              <stop key={i} offset={`${(i / Math.max(bodyColors.length - 1, 1)) * 100}%`} stopColor={c}/>
            ))}
          </linearGradient>
        )}
        <linearGradient id={`${id}_v`} x1="0" y1="0" x2="1" y2="1">
          {visorColors.map((c, i) => (
            <stop key={i} offset={`${(i / Math.max(visorColors.length - 1, 1)) * 100}%`} stopColor={c}/>
          ))}
        </linearGradient>
        <filter id={`${id}_g`}>
          <feGaussianBlur stdDeviation={glow} result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      <path d={CREWMATE_PATH} fill={`url(#${id}_b)`} filter={`url(#${id}_g)`} stroke={stroke} strokeWidth="0.5"/>
      <path d={VISOR_PATH} fill={`url(#${id}_v)`} opacity="0.9"/>
      {effects}
    </svg>
  );
}

// Reusable effect fragments
const sparkles = (color, opacity = 0.6) => (
  <>
    <circle cx="10" cy="10" r="0.7" fill="#FFFFFF" opacity={opacity}/>
    <circle cx="25" cy="8" r="0.5" fill={color} opacity={opacity * 0.8}/>
    <circle cx="12" cy="26" r="0.4" fill="#FFFFFF" opacity={opacity * 0.7}/>
  </>
);

const crown = (fillColor, strokeColor) => (
  <path d="M 12 4 L 14 -1 L 16 2 L 18 -2 L 20 2 L 22 -1 L 24 4 Z" fill={fillColor} stroke={strokeColor} strokeWidth="0.4"/>
);

const s1Badge = (color) => (
  <>
    <rect x="2" y="30" width="10" height="6" rx="1.5" fill={color} opacity="0.85"/>
    <text x="7" y="35" textAnchor="middle" fontSize="4.5" fontWeight="bold" fill="#FFFFFF" fontFamily="sans-serif">S1</text>
  </>
);

const s2Badge = (color) => (
  <>
    <rect x="2" y="30" width="10" height="6" rx="1.5" fill={color} opacity="0.85"/>
    <text x="7" y="35" textAnchor="middle" fontSize="4.5" fontWeight="bold" fill="#FFFFFF" fontFamily="sans-serif">S2</text>
  </>
);

const SVG_ITEMS = {
  // ─── MYTHIC (5) ──────────────────────────────────────────────
  mythic_1: ({ size = 48 }) => (
    <svg viewBox="0 0 36 40" width={size} height={size * 40/36}>
      <defs>
        <linearGradient id="m1_body" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FF4500"/>
          <stop offset="50%" stopColor="#FF6B00"/>
          <stop offset="100%" stopColor="#FFD700"/>
        </linearGradient>
        <linearGradient id="m1_visor" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FFE082"/>
          <stop offset="100%" stopColor="#FFF9C4"/>
        </linearGradient>
        <filter id="m1_glow">
          <feGaussianBlur stdDeviation="1.5" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      <path d={CREWMATE_PATH} fill="url(#m1_body)" filter="url(#m1_glow)" stroke="#FF2200" strokeWidth="0.5"/>
      <path d={VISOR_PATH} fill="url(#m1_visor)" opacity="0.9"/>
      <path d="M 10 8 Q 8 3 12 1 Q 10 5 13 6" fill="#FFD700" opacity="0.7"/>
      <path d="M 24 6 Q 26 1 22 0 Q 25 4 22 5" fill="#FF6B00" opacity="0.6"/>
      <path d="M 17 3 Q 16 -1 19 -2 Q 17 2 20 2" fill="#FFAB00" opacity="0.5"/>
    </svg>
  ),

  mythic_2: ({ size = 48 }) => (
    <svg viewBox="0 0 36 40" width={size} height={size * 40/36}>
      <defs>
        <linearGradient id="m2_body" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#4FC3F7"/>
          <stop offset="40%" stopColor="#B2EBF2"/>
          <stop offset="70%" stopColor="#E1BEE7"/>
          <stop offset="100%" stopColor="#CE93D8"/>
        </linearGradient>
        <linearGradient id="m2_visor" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#E0F7FA"/>
          <stop offset="100%" stopColor="#FFFFFF"/>
        </linearGradient>
        <filter id="m2_sparkle">
          <feGaussianBlur stdDeviation="0.8" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      <path d={CREWMATE_PATH} fill="url(#m2_body)" filter="url(#m2_sparkle)" stroke="#80DEEA" strokeWidth="0.5"/>
      <path d={VISOR_PATH} fill="url(#m2_visor)" opacity="0.9"/>
      <line x1="12" y1="10" x2="14" y2="15" stroke="#FFFFFF" strokeWidth="0.5" opacity="0.6"/>
      <line x1="24" y1="12" x2="22" y2="18" stroke="#FFFFFF" strokeWidth="0.5" opacity="0.5"/>
      <line x1="16" y1="20" x2="20" y2="25" stroke="#E0F7FA" strokeWidth="0.4" opacity="0.4"/>
      <circle cx="13" cy="22" r="1" fill="#FFFFFF" opacity="0.7"/>
      <circle cx="23" cy="16" r="0.8" fill="#FFFFFF" opacity="0.5"/>
    </svg>
  ),

  mythic_3: ({ size = 48 }) => (
    <svg viewBox="0 0 36 40" width={size} height={size * 40/36}>
      <defs>
        <radialGradient id="m3_body" cx="0.5" cy="0.5" r="0.6">
          <stop offset="0%" stopColor="#4A148C"/>
          <stop offset="60%" stopColor="#1A0033"/>
          <stop offset="100%" stopColor="#000000"/>
        </radialGradient>
        <linearGradient id="m3_visor" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#7C4DFF"/>
          <stop offset="100%" stopColor="#B388FF"/>
        </linearGradient>
        <filter id="m3_void">
          <feGaussianBlur stdDeviation="1.2" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      <path d={CREWMATE_PATH} fill="url(#m3_body)" filter="url(#m3_void)" stroke="#7C4DFF" strokeWidth="0.5"/>
      <path d={VISOR_PATH} fill="url(#m3_visor)" opacity="0.85"/>
      <circle cx="10" cy="14" r="1.2" fill="#7C4DFF" opacity="0.4"/>
      <circle cx="26" cy="20" r="0.8" fill="#B388FF" opacity="0.3"/>
      <circle cx="14" cy="28" r="1" fill="#651FFF" opacity="0.35"/>
      <circle cx="22" cy="10" r="0.6" fill="#D1C4E9" opacity="0.25"/>
    </svg>
  ),

  mythic_4: ({ size = 48 }) => (
    <svg viewBox="0 0 36 40" width={size} height={size * 40/36}>
      <defs>
        <linearGradient id="m4_body" x1="0" y1="0" x2="0.5" y2="1">
          <stop offset="0%" stopColor="#1565C0"/>
          <stop offset="50%" stopColor="#0D47A1"/>
          <stop offset="100%" stopColor="#263238"/>
        </linearGradient>
        <linearGradient id="m4_visor" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#64B5F6"/>
          <stop offset="100%" stopColor="#E3F2FD"/>
        </linearGradient>
        <filter id="m4_storm">
          <feGaussianBlur stdDeviation="1" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      <path d={CREWMATE_PATH} fill="url(#m4_body)" filter="url(#m4_storm)" stroke="#42A5F5" strokeWidth="0.5"/>
      <path d={VISOR_PATH} fill="url(#m4_visor)" opacity="0.9"/>
      <path d="M 12 5 L 10 10 L 13 9 L 11 14" fill="none" stroke="#FFD600" strokeWidth="1" opacity="0.8"/>
      <path d="M 25 7 L 27 12 L 24 11 L 26 15" fill="none" stroke="#FFEB3B" strokeWidth="0.8" opacity="0.6"/>
    </svg>
  ),

  mythic_5: ({ size = 48 }) => (
    <svg viewBox="0 0 36 40" width={size} height={size * 40/36}>
      <defs>
        <linearGradient id="m5_body" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FFD700"/>
          <stop offset="40%" stopColor="#FFC107"/>
          <stop offset="100%" stopColor="#FF8F00"/>
        </linearGradient>
        <linearGradient id="m5_visor" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FFFDE7"/>
          <stop offset="100%" stopColor="#FFF9C4"/>
        </linearGradient>
        <filter id="m5_shine">
          <feGaussianBlur stdDeviation="1.5" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      <path d={CREWMATE_PATH} fill="url(#m5_body)" filter="url(#m5_shine)" stroke="#FFB300" strokeWidth="0.5"/>
      <path d={VISOR_PATH} fill="url(#m5_visor)" opacity="0.9"/>
      {crown('#FFD700', '#FF8F00')}
      <line x1="8" y1="15" x2="5" y2="14" stroke="#FFECB3" strokeWidth="0.5" opacity="0.5"/>
      <line x1="28" y1="15" x2="31" y2="14" stroke="#FFECB3" strokeWidth="0.5" opacity="0.5"/>
    </svg>
  ),

  // ─── GALAXY (5) ──────────────────────────────────────────────
  galaxy_1: ({ size = 48 }) => (
    <svg viewBox="0 0 36 40" width={size} height={size * 40/36}>
      <defs>
        <radialGradient id="g1_body" cx="0.4" cy="0.4" r="0.7">
          <stop offset="0%" stopColor="#E040FB"/>
          <stop offset="40%" stopColor="#7B1FA2"/>
          <stop offset="100%" stopColor="#1A237E"/>
        </radialGradient>
        <linearGradient id="g1_visor" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#F48FB1"/>
          <stop offset="100%" stopColor="#FCE4EC"/>
        </linearGradient>
        <filter id="g1_nebula">
          <feGaussianBlur stdDeviation="2" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      <path d={CREWMATE_PATH} fill="url(#g1_body)" filter="url(#g1_nebula)" stroke="#E040FB" strokeWidth="0.6"/>
      <path d={VISOR_PATH} fill="url(#g1_visor)" opacity="0.9"/>
      <ellipse cx="12" cy="16" rx="4" ry="2" fill="#F48FB1" opacity="0.15" transform="rotate(-30 12 16)"/>
      <ellipse cx="22" cy="22" rx="3" ry="1.5" fill="#CE93D8" opacity="0.12" transform="rotate(20 22 22)"/>
      {sparkles('#FFF9C4')}
    </svg>
  ),

  galaxy_2: ({ size = 48 }) => (
    <svg viewBox="0 0 36 40" width={size} height={size * 40/36}>
      <defs>
        <radialGradient id="g2_body" cx="0.5" cy="0.4" r="0.6">
          <stop offset="0%" stopColor="#FFFFFF"/>
          <stop offset="30%" stopColor="#FFF176"/>
          <stop offset="60%" stopColor="#FF6F00"/>
          <stop offset="100%" stopColor="#BF360C"/>
        </radialGradient>
        <linearGradient id="g2_visor" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FFFFFF"/>
          <stop offset="100%" stopColor="#FFF9C4"/>
        </linearGradient>
        <filter id="g2_explode">
          <feGaussianBlur stdDeviation="2.5" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      <path d={CREWMATE_PATH} fill="url(#g2_body)" filter="url(#g2_explode)" stroke="#FF6F00" strokeWidth="0.5"/>
      <path d={VISOR_PATH} fill="url(#g2_visor)" opacity="0.95"/>
      <line x1="18" y1="2" x2="18" y2="-3" stroke="#FFD600" strokeWidth="1" opacity="0.6"/>
      <line x1="8" y1="8" x2="4" y2="4" stroke="#FFAB00" strokeWidth="0.8" opacity="0.5"/>
      <line x1="28" y1="8" x2="32" y2="4" stroke="#FFAB00" strokeWidth="0.8" opacity="0.5"/>
      <line x1="5" y1="20" x2="1" y2="20" stroke="#FF8F00" strokeWidth="0.6" opacity="0.4"/>
      <line x1="31" y1="20" x2="35" y2="20" stroke="#FF8F00" strokeWidth="0.6" opacity="0.4"/>
    </svg>
  ),

  galaxy_3: ({ size = 48 }) => (
    <svg viewBox="0 0 36 40" width={size} height={size * 40/36}>
      <defs>
        <linearGradient id="g3_body" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#00E5FF"/>
          <stop offset="50%" stopColor="#1DE9B6"/>
          <stop offset="100%" stopColor="#00BFA5"/>
        </linearGradient>
        <linearGradient id="g3_visor" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#E0F7FA"/>
          <stop offset="100%" stopColor="#FFFFFF"/>
        </linearGradient>
        <filter id="g3_beacon">
          <feGaussianBlur stdDeviation="2" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      <path d={CREWMATE_PATH} fill="url(#g3_body)" filter="url(#g3_beacon)" stroke="#00E5FF" strokeWidth="0.6"/>
      <path d={VISOR_PATH} fill="url(#g3_visor)" opacity="0.9"/>
      <path d="M 15 0 L 18 6 L 21 0" fill="#00E5FF" opacity="0.3"/>
      <line x1="18" y1="0" x2="18" y2="-5" stroke="#00E5FF" strokeWidth="1.5" opacity="0.4"/>
      {sparkles('#B2FF59')}
    </svg>
  ),

  galaxy_4: ({ size = 48 }) => (
    <svg viewBox="0 0 36 40" width={size} height={size * 40/36}>
      <defs>
        <radialGradient id="g4_body" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#37474F"/>
          <stop offset="60%" stopColor="#1A1A2E"/>
          <stop offset="100%" stopColor="#000000"/>
        </radialGradient>
        <linearGradient id="g4_visor" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#455A64"/>
          <stop offset="100%" stopColor="#78909C"/>
        </linearGradient>
        <filter id="g4_dark">
          <feGaussianBlur stdDeviation="1.8" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      <path d={CREWMATE_PATH} fill="url(#g4_body)" filter="url(#g4_dark)" stroke="#455A64" strokeWidth="0.5"/>
      <path d={VISOR_PATH} fill="url(#g4_visor)" opacity="0.8"/>
      <path d="M 8 12 Q 5 15 8 20" fill="none" stroke="#546E7A" strokeWidth="0.8" opacity="0.4"/>
      <path d="M 28 14 Q 31 18 28 22" fill="none" stroke="#455A64" strokeWidth="0.6" opacity="0.3"/>
      <circle cx="9" cy="8" r="0.5" fill="#FFFFFF" opacity="0.15"/>
      <circle cx="27" cy="10" r="0.4" fill="#FFFFFF" opacity="0.1"/>
      <circle cx="15" cy="32" r="0.3" fill="#ECEFF1" opacity="0.12"/>
    </svg>
  ),

  galaxy_5: ({ size = 48 }) => (
    <svg viewBox="0 0 36 40" width={size} height={size * 40/36}>
      <defs>
        <radialGradient id="g5_body" cx="0.5" cy="0.45" r="0.5">
          <stop offset="0%" stopColor="#000000"/>
          <stop offset="40%" stopColor="#311B92"/>
          <stop offset="80%" stopColor="#6200EA"/>
          <stop offset="100%" stopColor="#AA00FF"/>
        </radialGradient>
        <linearGradient id="g5_visor" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#EA80FC"/>
          <stop offset="100%" stopColor="#E1BEE7"/>
        </linearGradient>
        <filter id="g5_warp">
          <feGaussianBlur stdDeviation="2.2" result="blur"/>
          <feComposite in="SourceGraphic" in2="blur" operator="over"/>
        </filter>
      </defs>
      <path d={CREWMATE_PATH} fill="url(#g5_body)" filter="url(#g5_warp)" stroke="#AA00FF" strokeWidth="0.6"/>
      <path d={VISOR_PATH} fill="url(#g5_visor)" opacity="0.85"/>
      <ellipse cx="18" cy="18" rx="16" ry="4" fill="none" stroke="#D500F9" strokeWidth="0.8" opacity="0.3" transform="rotate(-15 18 18)"/>
      <ellipse cx="18" cy="18" rx="13" ry="3" fill="none" stroke="#EA80FC" strokeWidth="0.5" opacity="0.2" transform="rotate(-15 18 18)"/>
      <circle cx="8" cy="18" r="0.6" fill="#EA80FC" opacity="0.4"/>
      <circle cx="28" cy="18" r="0.5" fill="#D500F9" opacity="0.3"/>
    </svg>
  ),

  // ─── CREWPASS ITEMS (12) — blue crewmates, escalating intensity ───
  crew_pass_10: makeCrewmate('cp10', {
    bodyColors: ['#90CAF9', '#64B5F6', '#42A5F5'],
    visorColors: ['#BBDEFB', '#E3F2FD'],
    stroke: '#1E88E5', glow: 0.6,
  }),
  crew_pass_20: makeCrewmate('cp20', {
    bodyColors: ['#64B5F6', '#42A5F5', '#1E88E5'],
    visorColors: ['#BBDEFB', '#E3F2FD'],
    stroke: '#1565C0', glow: 0.8,
  }),
  crew_pass_25: makeCrewmate('cp25', {
    bodyColors: ['#42A5F5', '#1E88E5', '#1565C0'],
    visorColors: ['#E3F2FD', '#FFFFFF'],
    stroke: '#0D47A1', glow: 1.2,
    effects: sparkles('#BBDEFB', 0.7),
  }),
  crew_pass_30: makeCrewmate('cp30', {
    bodyColors: ['#1E88E5', '#1565C0', '#0D47A1'],
    visorColors: ['#90CAF9', '#E3F2FD'],
    stroke: '#0D47A1', glow: 0.8,
  }),
  crew_pass_40: makeCrewmate('cp40', {
    bodyColors: ['#0097A7', '#00838F', '#006064'],
    visorColors: ['#B2EBF2', '#E0F7FA'],
    stroke: '#006064', glow: 1,
    effects: <><line x1="8" y1="12" x2="5" y2="10" stroke="#4DD0E1" strokeWidth="0.5" opacity="0.5"/><line x1="28" y1="14" x2="31" y2="12" stroke="#4DD0E1" strokeWidth="0.5" opacity="0.4"/></>,
  }),
  crew_pass_50: makeCrewmate('cp50', {
    bodyColors: ['#00BCD4', '#0097A7', '#01579B'],
    visorColors: ['#E0F7FA', '#FFFFFF'],
    stroke: '#006064', glow: 1.5,
    effects: <>{sparkles('#80DEEA', 0.8)}<path d="M 12 5 L 10 10 L 13 9 L 11 14" fill="none" stroke="#4DD0E1" strokeWidth="0.6" opacity="0.5"/></>,
  }),
  crew_pass_60: makeCrewmate('cp60', {
    bodyColors: ['#1565C0', '#0D47A1', '#1A237E'],
    visorColors: ['#64B5F6', '#BBDEFB'],
    stroke: '#1A237E', glow: 1,
    effects: <><line x1="12" y1="10" x2="14" y2="16" stroke="#FFFFFF" strokeWidth="0.4" opacity="0.3"/><line x1="22" y1="12" x2="20" y2="20" stroke="#FFFFFF" strokeWidth="0.3" opacity="0.25"/></>,
  }),
  crew_pass_70: makeCrewmate('cp70', {
    bodyColors: ['#0D47A1', '#1A237E', '#0D47A1'],
    visorColors: ['#42A5F5', '#90CAF9'],
    stroke: '#1A237E', glow: 1.2,
    effects: <><circle cx="10" cy="20" r="1" fill="#64B5F6" opacity="0.3"/><circle cx="26" cy="16" r="0.8" fill="#42A5F5" opacity="0.25"/></>,
  }),
  crew_pass_75: makeCrewmate('cp75', {
    bodyColors: ['#1A237E', '#283593', '#3F51B5'],
    visorColors: ['#9FA8DA', '#E8EAF6'],
    stroke: '#1A237E', glow: 1.5,
    effects: <>{sparkles('#9FA8DA', 0.8)}<circle cx="18" cy="2" r="1.5" fill="#7986CB" opacity="0.3"/></>,
  }),
  crew_pass_80: makeCrewmate('cp80', {
    bodyColors: ['#283593', '#1A237E', '#311B92'],
    visorColors: ['#7986CB', '#C5CAE9'],
    stroke: '#1A237E', glow: 1.3,
    effects: <><path d="M 8 8 Q 6 4 10 2" fill="none" stroke="#7986CB" strokeWidth="0.6" opacity="0.4"/><path d="M 28 10 Q 30 6 26 4" fill="none" stroke="#5C6BC0" strokeWidth="0.5" opacity="0.35"/></>,
  }),
  crew_pass_90: makeCrewmate('cp90', {
    bodyColors: ['#1A237E', '#0D47A1', '#00BCD4'],
    visorColors: ['#80DEEA', '#FFFFFF'],
    stroke: '#1A237E', glow: 2,
    effects: <>{sparkles('#80DEEA', 0.9)}<path d="M 12 5 L 10 10 L 13 9 L 11 14" fill="none" stroke="#00E5FF" strokeWidth="0.8" opacity="0.6"/><path d="M 25 3 L 27 8 L 24 7 L 26 11" fill="none" stroke="#00BCD4" strokeWidth="0.6" opacity="0.5"/></>,
  }),
  crew_pass_100: makeCrewmate('cp100', {
    bodyColors: ['#E3F2FD', '#42A5F5', '#1565C0'],
    visorColors: ['#FFFFFF', '#E3F2FD'],
    stroke: '#0D47A1', glow: 2.5,
    effects: <>{crown('#42A5F5', '#1565C0')}{sparkles('#E3F2FD', 1)}<line x1="6" y1="18" x2="2" y2="17" stroke="#90CAF9" strokeWidth="0.6" opacity="0.5"/><line x1="30" y1="18" x2="34" y2="17" stroke="#90CAF9" strokeWidth="0.6" opacity="0.5"/></>,
  }),

  // ─── IMPOSTOR PASS ITEMS (12) — red/dark crewmates, escalating intensity ───
  impostor_pass_10: makeCrewmate('ip10', {
    bodyColors: ['#E57373', '#EF5350', '#F44336'],
    visorColors: ['#FFCDD2', '#FFEBEE'],
    stroke: '#C62828', glow: 0.6,
  }),
  impostor_pass_20: makeCrewmate('ip20', {
    bodyColors: ['#EF5350', '#E53935', '#D32F2F'],
    visorColors: ['#FFCDD2', '#FFEBEE'],
    stroke: '#B71C1C', glow: 0.8,
  }),
  impostor_pass_25: makeCrewmate('ip25', {
    bodyColors: ['#D32F2F', '#C62828', '#B71C1C'],
    visorColors: ['#EF9A9A', '#FFCDD2'],
    stroke: '#B71C1C', glow: 1.2,
    effects: <><circle cx="10" cy="28" r="1" fill="#F44336" opacity="0.5"/><circle cx="22" cy="30" r="0.7" fill="#E53935" opacity="0.4"/><circle cx="16" cy="32" r="0.5" fill="#D32F2F" opacity="0.35"/></>,
  }),
  impostor_pass_30: makeCrewmate('ip30', {
    bodyColors: ['#C62828', '#B71C1C', '#880E4F'],
    visorColors: ['#EF5350', '#FFCDD2'],
    stroke: '#880E4F', glow: 0.8,
  }),
  impostor_pass_40: makeCrewmate('ip40', {
    bodyColors: ['#B71C1C', '#880E4F', '#4A0000'],
    visorColors: ['#E57373', '#FFCDD2'],
    stroke: '#4A0000', glow: 1,
    effects: <><path d="M 6 14 Q 4 18 7 22" fill="none" stroke="#4A0000" strokeWidth="0.6" opacity="0.3"/><path d="M 30 16 Q 32 20 29 24" fill="none" stroke="#4A0000" strokeWidth="0.5" opacity="0.25"/></>,
  }),
  impostor_pass_50: makeCrewmate('ip50', {
    bodyColors: ['#4A0000', '#B71C1C', '#4A0000'],
    visorColors: ['#F44336', '#FF8A80'],
    stroke: '#4A0000', glow: 1.5, radial: true,
    effects: <>{sparkles('#F44336', 0.6)}<circle cx="18" cy="6" r="2" fill="#4A0000" opacity="0.3"/></>,
  }),
  impostor_pass_60: makeCrewmate('ip60', {
    bodyColors: ['#880E4F', '#4A148C', '#1A0000'],
    visorColors: ['#CE93D8', '#F8BBD0'],
    stroke: '#4A148C', glow: 1,
    effects: <><path d="M 9 10 Q 7 14 10 18" fill="none" stroke="#880E4F" strokeWidth="0.5" opacity="0.35"/><path d="M 27 12 Q 29 16 26 20" fill="none" stroke="#4A148C" strokeWidth="0.4" opacity="0.3"/></>,
  }),
  impostor_pass_70: makeCrewmate('ip70', {
    bodyColors: ['#4A148C', '#311B92', '#1A0000'],
    visorColors: ['#B388FF', '#E1BEE7'],
    stroke: '#1A0000', glow: 1.2,
    effects: <><circle cx="12" cy="22" r="1.2" fill="#7C4DFF" opacity="0.2"/><circle cx="24" cy="18" r="0.9" fill="#B388FF" opacity="0.15"/></>,
  }),
  impostor_pass_75: makeCrewmate('ip75', {
    bodyColors: ['#1A0000', '#4A148C', '#000000'],
    visorColors: ['#EA80FC', '#E1BEE7'],
    stroke: '#000000', glow: 1.8, radial: true,
    effects: <>{sparkles('#EA80FC', 0.7)}<circle cx="18" cy="20" r="3" fill="#7C4DFF" opacity="0.1"/></>,
  }),
  impostor_pass_80: makeCrewmate('ip80', {
    bodyColors: ['#311B92', '#1A0033', '#000000'],
    visorColors: ['#D500F9', '#F48FB1'],
    stroke: '#000000', glow: 1.3,
    effects: <><path d="M 10 6 Q 8 2 12 0" fill="none" stroke="#D500F9" strokeWidth="0.5" opacity="0.4"/><path d="M 26 8 Q 28 4 24 2" fill="none" stroke="#AA00FF" strokeWidth="0.4" opacity="0.3"/></>,
  }),
  impostor_pass_90: makeCrewmate('ip90', {
    bodyColors: ['#B71C1C', '#4A0000', '#000000'],
    visorColors: ['#FF5252', '#FF8A80'],
    stroke: '#000000', glow: 2,
    effects: <>{sparkles('#FF5252', 0.8)}<path d="M 10 6 Q 8 1 13 -1" fill="#FF5252" opacity="0.3"/><path d="M 24 4 Q 26 -1 21 -2" fill="#B71C1C" opacity="0.25"/></>,
  }),
  impostor_pass_100: makeCrewmate('ip100', {
    bodyColors: ['#000000', '#4A0000', '#000000'],
    visorColors: ['#FF1744', '#FF5252'],
    stroke: '#4A0000', glow: 2.5, radial: true,
    effects: <>{crown('#B71C1C', '#4A0000')}{sparkles('#FF1744', 1)}<path d="M 6 16 Q 2 18 6 22" fill="none" stroke="#FF1744" strokeWidth="0.6" opacity="0.4"/><path d="M 30 16 Q 34 18 30 22" fill="none" stroke="#FF1744" strokeWidth="0.6" opacity="0.4"/></>,
  }),

  // ─── S1 RANK REWARD ITEMS (10) — colored by rank, with S1 badge ───
  s1_rank_crewling: makeCrewmate('sr0', {
    bodyColors: ['#9E9E9E', '#8B8B8B', '#757575'],
    visorColors: ['#E0E0E0', '#F5F5F5'],
    stroke: '#616161', glow: 0.5,
    effects: s1Badge('#616161'),
  }),
  s1_rank_wood: makeCrewmate('sr1', {
    bodyColors: ['#A1887F', '#8B4513', '#6D4C41'],
    visorColors: ['#D7CCC8', '#EFEBE9'],
    stroke: '#4E342E', glow: 0.5,
    effects: s1Badge('#6D4C41'),
  }),
  s1_rank_copper: makeCrewmate('sr2', {
    bodyColors: ['#D4A574', '#B87333', '#8D5524'],
    visorColors: ['#FFCCBC', '#FBE9E7'],
    stroke: '#8D5524', glow: 0.7,
    effects: s1Badge('#8D5524'),
  }),
  s1_rank_bronze: makeCrewmate('sr3', {
    bodyColors: ['#E6A44E', '#CD7F32', '#A0522D'],
    visorColors: ['#FFE0B2', '#FFF3E0'],
    stroke: '#A0522D', glow: 0.8,
    effects: s1Badge('#A0522D'),
  }),
  s1_rank_silver: makeCrewmate('sr4', {
    bodyColors: ['#E0E0E0', '#C0C0C0', '#9E9E9E'],
    visorColors: ['#F5F5F5', '#FFFFFF'],
    stroke: '#757575', glow: 1,
    effects: <>{s1Badge('#757575')}{sparkles('#E0E0E0', 0.3)}</>,
  }),
  s1_rank_gold: makeCrewmate('sr5', {
    bodyColors: ['#FFE082', '#FFD700', '#FFC107'],
    visorColors: ['#FFFDE7', '#FFFFFF'],
    stroke: '#FF8F00', glow: 1.2,
    effects: <>{s1Badge('#FF8F00')}{sparkles('#FFD700', 0.4)}</>,
  }),
  s1_rank_diamond: makeCrewmate('sr6', {
    bodyColors: ['#E0F7FA', '#B9F2FF', '#80DEEA'],
    visorColors: ['#FFFFFF', '#E0F7FA'],
    stroke: '#4DD0E1', glow: 1.5,
    effects: <>{s1Badge('#0097A7')}{sparkles('#B9F2FF', 0.6)}<line x1="12" y1="12" x2="14" y2="18" stroke="#FFFFFF" strokeWidth="0.4" opacity="0.4"/><line x1="22" y1="14" x2="20" y2="20" stroke="#FFFFFF" strokeWidth="0.3" opacity="0.3"/></>,
  }),
  s1_rank_master: makeCrewmate('sr7', {
    bodyColors: ['#EF5350', '#E53935', '#C62828'],
    visorColors: ['#FFCDD2', '#FFEBEE'],
    stroke: '#B71C1C', glow: 1.5,
    effects: <>{s1Badge('#B71C1C')}{sparkles('#EF5350', 0.5)}</>,
  }),
  s1_rank_grandmaster: makeCrewmate('sr8', {
    bodyColors: ['#CE93D8', '#AB47BC', '#8E24AA'],
    visorColors: ['#F3E5F5', '#FFFFFF'],
    stroke: '#6A1B9A', glow: 1.8,
    effects: <>{s1Badge('#6A1B9A')}{sparkles('#CE93D8', 0.7)}<circle cx="18" cy="3" r="1.5" fill="#AB47BC" opacity="0.3"/></>,
  }),
  s1_rank_lord: makeCrewmate('sr9', {
    bodyColors: ['#FFD700', '#FFC107', '#FF8F00'],
    visorColors: ['#FFFDE7', '#FFFFFF'],
    stroke: '#FF6F00', glow: 2.2,
    effects: <>{s1Badge('#FF6F00')}{crown('#FFD700', '#FF8F00')}{sparkles('#FFD700', 0.9)}<line x1="6" y1="18" x2="2" y2="17" stroke="#FFECB3" strokeWidth="0.6" opacity="0.5"/><line x1="30" y1="18" x2="34" y2="17" stroke="#FFECB3" strokeWidth="0.6" opacity="0.5"/></>,
  }),

  // ─── S2 RANK REWARD ITEMS (10) — colored by rank, with S2 badge ───
  s2_rank_crewling: makeCrewmate('s2r0', {
    bodyColors: ['#9E9E9E', '#8B8B8B', '#757575'],
    visorColors: ['#E0E0E0', '#F5F5F5'],
    stroke: '#616161', glow: 0.5,
    effects: s2Badge('#616161'),
  }),
  s2_rank_wood: makeCrewmate('s2r1', {
    bodyColors: ['#A1887F', '#8B4513', '#6D4C41'],
    visorColors: ['#D7CCC8', '#EFEBE9'],
    stroke: '#4E342E', glow: 0.5,
    effects: s2Badge('#6D4C41'),
  }),
  s2_rank_copper: makeCrewmate('s2r2', {
    bodyColors: ['#D4A574', '#B87333', '#8D5524'],
    visorColors: ['#FFCCBC', '#FBE9E7'],
    stroke: '#8D5524', glow: 0.7,
    effects: s2Badge('#8D5524'),
  }),
  s2_rank_bronze: makeCrewmate('s2r3', {
    bodyColors: ['#E6A44E', '#CD7F32', '#A0522D'],
    visorColors: ['#FFE0B2', '#FFF3E0'],
    stroke: '#A0522D', glow: 0.8,
    effects: s2Badge('#A0522D'),
  }),
  s2_rank_silver: makeCrewmate('s2r4', {
    bodyColors: ['#E0E0E0', '#C0C0C0', '#9E9E9E'],
    visorColors: ['#F5F5F5', '#FFFFFF'],
    stroke: '#757575', glow: 1,
    effects: <>{s2Badge('#757575')}{sparkles('#E0E0E0', 0.3)}</>,
  }),
  s2_rank_gold: makeCrewmate('s2r5', {
    bodyColors: ['#FFE082', '#FFD700', '#FFC107'],
    visorColors: ['#FFFDE7', '#FFFFFF'],
    stroke: '#FF8F00', glow: 1.2,
    effects: <>{s2Badge('#FF8F00')}{sparkles('#FFD700', 0.4)}</>,
  }),
  s2_rank_diamond: makeCrewmate('s2r6', {
    bodyColors: ['#E0F7FA', '#B9F2FF', '#80DEEA'],
    visorColors: ['#FFFFFF', '#E0F7FA'],
    stroke: '#4DD0E1', glow: 1.5,
    effects: <>{s2Badge('#0097A7')}{sparkles('#B9F2FF', 0.6)}<line x1="12" y1="12" x2="14" y2="18" stroke="#FFFFFF" strokeWidth="0.4" opacity="0.4"/><line x1="22" y1="14" x2="20" y2="20" stroke="#FFFFFF" strokeWidth="0.3" opacity="0.3"/></>,
  }),
  s2_rank_master: makeCrewmate('s2r7', {
    bodyColors: ['#EF5350', '#E53935', '#C62828'],
    visorColors: ['#FFCDD2', '#FFEBEE'],
    stroke: '#B71C1C', glow: 1.5,
    effects: <>{s2Badge('#B71C1C')}{sparkles('#EF5350', 0.5)}</>,
  }),
  s2_rank_grandmaster: makeCrewmate('s2r8', {
    bodyColors: ['#CE93D8', '#AB47BC', '#8E24AA'],
    visorColors: ['#F3E5F5', '#FFFFFF'],
    stroke: '#6A1B9A', glow: 1.8,
    effects: <>{s2Badge('#6A1B9A')}{sparkles('#CE93D8', 0.7)}<circle cx="18" cy="3" r="1.5" fill="#AB47BC" opacity="0.3"/></>,
  }),
  s2_rank_lord: makeCrewmate('s2r9', {
    bodyColors: ['#FFD700', '#FFC107', '#FF8F00'],
    visorColors: ['#FFFDE7', '#FFFFFF'],
    stroke: '#FF6F00', glow: 2.2,
    effects: <>{s2Badge('#FF6F00')}{crown('#FFD700', '#FF8F00')}{sparkles('#FFD700', 0.9)}<line x1="6" y1="18" x2="2" y2="17" stroke="#FFECB3" strokeWidth="0.6" opacity="0.5"/><line x1="30" y1="18" x2="34" y2="17" stroke="#FFECB3" strokeWidth="0.6" opacity="0.5"/></>,
  }),
};

/**
 * Renders an item's icon — SVG art for special items, emoji for everything else.
 * @param {{ item: object, size?: number, style?: object }} props
 */
function ItemIcon({ item, size = 48, style = {} }) {
  const SvgComponent = SVG_ITEMS[item.id] || null;

  if (SvgComponent) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...style }}>
        <SvgComponent size={size} />
      </span>
    );
  }

  return (
    <span style={{ fontSize: `${size}px`, lineHeight: 1, ...style }}>
      {item.icon}
    </span>
  );
}

export { SVG_ITEMS };
export default ItemIcon;
